/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { useLocation } from "react-router-dom";

import { TenantTypesEnum } from "Constants/TenantTypes";

import { INavigationLinkProps } from "./INavigationLinkProps";
import {
  AnchorLinkWrapper,
  AnchorWrapper,
  Image,
  LinkWrapper,
  NavLink,
  SubLinkWrapper,
  Tooltip,
} from "./NavigationLink.styles";

/**
 * The Navigation Link component.
 * @param props {INavigationLinkProps}
 * @returns {React.ReactElement} The component.
 */
const NavigationLink = (props: INavigationLinkProps): React.ReactElement => {
  const location = useLocation();
  const { header, icon, source, tenant, text, isPrivacyLink, subLinks } = props;

  const firstSubLink = subLinks?.[0];
  const isActiveTenant =
    location.pathname.toLowerCase().includes(tenant.toLowerCase()) ||
    (location.pathname.toLowerCase().includes("studentambassadors") &&
      tenant === TenantTypesEnum.MSP);
  const hasActiveChild = subLinks?.some((subLink) => subLink.source === location.pathname) || false;

  /**
   * TODO - update activeTenant to compare tenant against tenant that comes from route
      once that is in place in our routing
   */
  return !isPrivacyLink ? (
    <>
      <LinkWrapper
        $isActive={location.pathname === source}
        $isActiveTenant={isActiveTenant}
        $hasActiveChild={hasActiveChild}
        to={{ pathname: firstSubLink ? firstSubLink?.source : source }}
      >
        {icon && (
          <Image
            active={location.pathname === source}
            alt={`${text} menu icon`}
            src={icon}
            $isActiveTenant={isActiveTenant}
          />
        )}
        <NavLink active={location.pathname === source} header={header}>
          {text}
        </NavLink>
        <Tooltip>{text}</Tooltip>
      </LinkWrapper>
      {subLinks?.map((subLink, index) => (
        <SubLinkWrapper
          key={index}
          $isActiveTenant={isActiveTenant}
          $hasActiveParent={hasActiveChild}
        >
          <NavigationLink {...subLink} />
        </SubLinkWrapper>
      ))}
    </>
  ) : (
    <AnchorWrapper href={source} target={"_"} $isActive={false} $isActiveTenant={isActiveTenant}>
      <Image active={false} alt={`${text} menu icon`} src={icon} $isActiveTenant={isActiveTenant} />
      <AnchorLinkWrapper>{text}</AnchorLinkWrapper>
      <Tooltip>{text}</Tooltip>
    </AnchorWrapper>
  );
};

export default NavigationLink;
